import { yupResolver } from '@hookform/resolvers/yup';
import { ListingType } from '@thurstech/commons/dist/entity/listing';
import { Resources, SlotWithResources, VenueType } from '@thurstech/commons/dist/entity/venue';
import { TicketBookingVenueMeta } from '@thurstech/commons/dist/models/listing';
import { getPrettyDateString } from '@thurstech/commons/dist/utils/util';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';

import { PackageCard } from './PackageCard';
import CalendarIcon from '../../../assets/svg/CalenderIcon';
import MapIcon from '../../../assets/svg/MapIcon';
import Button from '../../../components/button/Button';
import Input from '../../../components/input/Input';
import InputSelect, { Option } from '../../../components/input/InputSelect';
import { useOnScreen } from '../../../hooks/common';
import { useAppDispatch, useAppSelector } from '../../../hooks/store';
import { isThunkLoading } from '../../../redux/common';
import {
  updateVenuePackagesThunk,
  getPackageOverridesThunk,
  updatePackageOverridesThunk,
  updateSlotThunk
} from '../../../redux/reducer/listingSlice';
import { getBookedTicketsThunk } from '../../../redux/reducer/propSlice';
import { ProductVenue } from '../../../utils/models/listing';
import { withErrorHandler } from '../../../utils/utils';
import { packageDetailSchema, VenueCardFormData } from '../../../validation/operations/products/venueCard';

interface VenueCardProps {
  venue: ProductVenue;
  productId: string;
  listingType: ListingType;
}

export const VenueCard: React.FC<VenueCardProps> = ({ venue, productId, listingType }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading } = useAppSelector(state => state.listing);
  const isResourcesLinkedToVenue = (venue as TicketBookingVenueMeta).isResourcesLinkedToVenue;
  // This is used to ensure that we don't make unecerray API calls to backend and only call the booked tickets and override when the compoennt actually mounts
  const [ref, isVisible, hasBeenVisible] = useOnScreen({ rootMargin: '200px', threshold: 0.1 });

  const availableSlots = venue.venueType === VenueType.TICKET_BOOKING ? venue.slots || [] : [];

  const slotOptions: Option[] = availableSlots.map(slot => ({
    id: slot.id!,
    value: getPrettyDateString(slot.sessions[0][0])
  }));

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<VenueCardFormData>({
    resolver: yupResolver(packageDetailSchema),
    defaultValues: {
      slot: _.pick(availableSlots.at(0), 'id', 'resources'),
      packages: venue.packages.map(pkg => {
        return isResourcesLinkedToVenue
          ? {
              id: pkg.id!,
              price: pkg.price,
              discount: pkg.discount
            }
          : {
              id: pkg.id!,
              price: pkg.price,
              resources: { count: pkg.resources?.count ?? pkg.resources?.details?.length ?? 0 },
              discount: pkg.discount
            };
      }) as VenueCardFormData['packages']
    },
    context: { isResourcesLinkedToVenue: isResourcesLinkedToVenue }
  });
  const slotId = watch('slot.id');

  const onSaveDetails = async (data: VenueCardFormData) => {
    const slotId = data.slot.id;
    const packageOverrides = data.packages;

    await withErrorHandler(async () => {
      await dispatch(
        updatePackageOverridesThunk({
          listingId: productId,
          venueId: venue.id,
          data: {
            listingId: productId,
            venueId: venue.id,
            venueType: VenueType.TICKET_BOOKING,
            details: isResourcesLinkedToVenue
              ? packageOverrides.map(pkg => _.pick(pkg, ['id', 'price', 'discount']))
              : packageOverrides.map(pkg => _.pick(pkg, ['id', 'price', 'discount', 'resources'])),
            slotId
          }
        })
      ).unwrap();
      if (isResourcesLinkedToVenue) {
        await dispatch(
          updateSlotThunk({
            listingId: productId,
            venueId: venue.id,
            slot: { id: slotId, resources: data.slot.resources as Resources },
            isResourcesLinkedToVenue: true
          })
        ).unwrap();
      }
    }, 'Successfully saved package details');
  };

  useEffect(() => {
    if (slotId && isVisible && !hasBeenVisible) {
      const slot = availableSlots.find(slot => slot.id! === slotId)!;
      const fetchOverrides = async () => {
        const [packageOverrides, bookedTickets] = await Promise.all([
          dispatch(getPackageOverridesThunk({ listingId: productId, venueId: venue.id, slotId })).unwrap(),
          dispatch(getBookedTicketsThunk({ listingId: productId, venueId: venue.id, slot })).unwrap()
        ]);
        const overridesMap = _.keyBy(packageOverrides.overrides, 'packageId');
        const bookedTicketsMap = _.keyBy(bookedTickets.bookedTickets, 'packageId');

        if (isResourcesLinkedToVenue) {
          const slotWithReources = slot as SlotWithResources;
          setValue('slot.resources.count', slotWithReources?.resources?.count);
          setValue('slot.bookedTickets', bookedTicketsMap['null']?.count);
          setValue(
            'slot.availableTickets',
            (slotWithReources?.resources?.count ?? 0) - (bookedTicketsMap['null']?.count ?? 0)
          );
        }
        const updatedPackages = venue.packages.map(pkg => {
          const overrideDetail = overridesMap[pkg.id ?? '']?.detail;
          const bookedTickets = Number(bookedTicketsMap[pkg.id ?? '']?.count ?? 0);
          const resources = {
            count:
              overrideDetail.resources?.count ?? pkg.resources?.count ?? pkg.resources?.details?.length ?? 0
          };
          const basePkg = isResourcesLinkedToVenue
            ? {
                id: pkg.id ?? '',
                price: pkg.price,
                discount: pkg.discount,
                bookedTickets: bookedTicketsMap[pkg.id ?? '']?.count,
                resources: { count: undefined }
              }
            : {
                id: pkg.id ?? '',
                price: pkg.price,
                resources,
                bookedTickets: bookedTicketsMap[pkg.id ?? '']?.count,
                availableTickets: resources.count - bookedTickets,
                discount: pkg.discount
              };
          return overrideDetail ? { ...basePkg, ...overrideDetail } : basePkg;
        });

        setValue('packages', updatedPackages);
      };

      fetchOverrides();
    }
  }, [slotId, isVisible]);

  const totalTickets = watch(`slot.resources.count`);
  useEffect(() => {
    if (isResourcesLinkedToVenue && totalTickets) {
      setValue(`slot.availableTickets`, totalTickets - (watch('slot.bookedTickets') ?? 0));
    }
  }, [totalTickets]);

  const onBookingClick = () => {
    const slotId = watch('slot.id');
    const slot = availableSlots.find(s => s.id === slotId);

    const searchParams = new URLSearchParams();
    searchParams.set('listingId', productId);
    searchParams.set('venueId', venue.id);
    if (slot) {
      const batch = slot.sessions[0][0];
      const encodedSlot = decodeURIComponent(JSON.stringify(batch));
      searchParams.set('slot', encodedSlot);
    }

    navigate(`/bookings?${searchParams.toString()}`);
  };

  return (
    <div className="bg-zinc-50 p-6 rounded shadow-md mt-4" ref={ref}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">{venue.title}</h3>
        <div className="flex items-center gap-2">
          <CalendarIcon />
          {venue.venueType === VenueType.TICKET_BOOKING && availableSlots && (
            <InputSelect
              name="slot.id"
              label=""
              placeholder="Select Slot"
              options={slotOptions}
              register={register}
              errors={errors}
            />
          )}
          {venue.venueType !== VenueType.TICKET_BOOKING && <span>{getPrettyDateString(venue.openDate)}</span>}
          <MapIcon />
          <span>{venue.city}</span>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSaveDetails)}>
        {isResourcesLinkedToVenue && (
          <div className="flex w-2/3 gap-4 my-4">
            <Input
              register={register}
              errors={errors}
              topLabel
              label="Total Tickets"
              name={`slot.resources.count`}
              type="number"
              required
            />
            <Input
              register={register}
              errors={errors}
              topLabel
              label="Available Tickets"
              name={`slot.availableTickets`}
              type="number"
              readOnly
              required
            />
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-4">
          {listingType !== ListingType.RESTAURANT &&
            venue.packages.map((pkg, index) => (
              <PackageCard
                key={pkg.id}
                index={index}
                venueType={venue.venueType}
                useForm={{ register, errors, watch, setValue }}
                title={pkg.title}
                isResourcesLinkedToVenue={isResourcesLinkedToVenue}
              />
            ))}
        </div>
        <div className="flex justify-between items-center">
          <div className="gap-2 flex flex-row">
            {listingType !== ListingType.RESTAURANT && (
              <Button
                label="Save Changes"
                type="submit"
                className="bg-[#35E0A1] text-black px-4 py-2 rounded"
                isLoading={isThunkLoading(
                  isLoading,
                  updateVenuePackagesThunk,
                  getPackageOverridesThunk,
                  updatePackageOverridesThunk,
                  updateSlotThunk
                )}
              />
            )}
            <Button
              label="View Bookings"
              onClick={onBookingClick}
              className="bg-[#35E0A1] text-black px-4 py-2 rounded"
            />

            <Link
              to={`/product/${productId}/venue/${venue.id}`}
              className="bg-[#35E0A1] text-black px-4 py-2 rounded"
            >
              View / Edit Venue Details
            </Link>
          </div>
          {/* <div className="flex items-center">
            <span>Pause Bookings</span>
            <label className="toggle-switch ml-2">
              <input type="checkbox" />
              <span className="slider"></span>
            </label>
          </div> */}
        </div>
      </form>
    </div>
  );
};
